import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { useEffect, useRef, useState } from 'react';
import {
  Bold as BoldIcon,
  Code2,
  Eraser,
  Heading1,
  Heading2,
  Heading3,
  Image as ImageIcon,
  Italic as ItalicIcon,
  Link2,
  List as ListIcon,
  ListOrdered,
  Maximize2,
  Minimize2,
  Minus,
  Quote,
  Underline as UnderlineIcon,
} from 'lucide-react';

import { LinkModal, LinkModalValue } from './link-modal';

const labelClass = 'mb-2 block text-sm font-medium text-gray-700';

const CustomLink = Link.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      title: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute('title'),
        renderHTML: (attributes: Record<string, unknown>) =>
          attributes.title ? { title: attributes.title } : {},
      },
    };
  },
}).configure({ openOnClick: false, autolink: true });

const ToolbarButton = ({
  onClick,
  active,
  label,
  disabled,
  children,
}: {
  onClick: () => void;
  active?: boolean;
  label: string;
  disabled?: boolean;
  children: React.ReactNode;
}) => (
  <div className="group relative flex">
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={`flex h-8 w-8 items-center justify-center rounded-md transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
        active ? 'bg-red-50 text-red-600' : 'text-gray-600 hover:bg-gray-100'
      }`}
    >
      {children}
    </button>
    <span className="pointer-events-none absolute -top-8 left-1/2 z-20 -translate-x-1/2 whitespace-nowrap rounded-md bg-gray-900 px-2 py-1 text-[11px] text-white opacity-0 transition-opacity delay-150 group-hover:opacity-100">
      {label}
    </span>
  </div>
);

const ToolbarDivider = () => <div className="mx-1 h-5 w-px shrink-0 bg-gray-200" />;

export type RichTextEditorProps = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  minHeight?: number;
  error?: string;
};

const emptyLinkModalValue: LinkModalValue = { url: '', text: '', tooltip: '', openInNewTab: false };

export const RichTextEditor = ({
  label,
  value,
  onChange,
  placeholder,
  minHeight = 160,
  error,
}: RichTextEditorProps) => {
  const [fullscreen, setFullscreen] = useState(false);
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [linkModalValue, setLinkModalValue] = useState<LinkModalValue>(emptyLinkModalValue);
  const imageInputRef = useRef<HTMLInputElement>(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] }, link: false }),
      CustomLink,
      Image,
      Placeholder.configure({ placeholder }),
    ],
    content: value || '',
    onUpdate: ({ editor: e }) => onChange(e.getHTML()),
  });

  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if ((value || '') !== current) {
      editor.commands.setContent(value || '', { emitUpdate: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  useEffect(() => {
    if (!fullscreen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setFullscreen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
    };
  }, [fullscreen]);

  if (!editor) return null;

  const openLinkModal = () => {
    const { from, to, empty } = editor.state.selection;
    const attrs = editor.getAttributes('link');
    setLinkModalValue({
      url: (attrs.href as string) || '',
      text: empty ? '' : editor.state.doc.textBetween(from, to, ' '),
      tooltip: (attrs.title as string) || '',
      openInNewTab: attrs.target === '_blank',
    });
    setIsLinkModalOpen(true);
  };

  const applyLink = ({ url, text, tooltip, openInNewTab }: LinkModalValue) => {
    const attrs = {
      href: url,
      title: tooltip || null,
      target: openInNewTab ? '_blank' : null,
      rel: openInNewTab ? 'noopener noreferrer' : null,
    };
    const { from, to, empty } = editor.state.selection;
    const displayText = text.trim();
    const chain = editor.chain().focus();

    if (empty) {
      chain.insertContent([{ type: 'text', text: displayText || url, marks: [{ type: 'link', attrs }] }]);
    } else if (displayText) {
      chain.insertContentAt({ from, to }, [{ type: 'text', text: displayText, marks: [{ type: 'link', attrs }] }]);
    } else {
      chain.setLink(attrs);
    }

    chain.run();
    setIsLinkModalOpen(false);
  };

  const onImageFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        editor.chain().focus().setImage({ src: reader.result }).run();
      }
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  return (
    <div className="w-full">
      {!fullscreen && <label className={labelClass}>{label}</label>}
      <div
        className={
          fullscreen
            ? 'fixed inset-0 z-[1000] flex flex-col bg-white p-4'
            : `rounded-lg border focus-within:border-transparent focus-within:ring-2 focus-within:ring-red-500 ${
                error ? 'border-redBase' : 'border-gray-300'
              }`
        }
      >
        {fullscreen && <span className="mb-2 text-sm font-medium text-gray-700">{label}</span>}

        <div className="flex flex-wrap items-center gap-1 rounded-t-lg border-b border-gray-200 bg-gray-50 p-2">
          <ToolbarButton
            label="Жирный"
            active={editor.isActive('bold')}
            onClick={() => editor.chain().focus().toggleBold().run()}
          >
            <BoldIcon size={16} />
          </ToolbarButton>
          <ToolbarButton
            label="Курсив"
            active={editor.isActive('italic')}
            onClick={() => editor.chain().focus().toggleItalic().run()}
          >
            <ItalicIcon size={16} />
          </ToolbarButton>
          <ToolbarButton
            label="Подчёркнутый"
            active={editor.isActive('underline')}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
          >
            <UnderlineIcon size={16} />
          </ToolbarButton>

          <ToolbarDivider />

          <ToolbarButton
            label="Заголовок 1"
            active={editor.isActive('heading', { level: 1 })}
            onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          >
            <Heading1 size={16} />
          </ToolbarButton>
          <ToolbarButton
            label="Заголовок 2"
            active={editor.isActive('heading', { level: 2 })}
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          >
            <Heading2 size={16} />
          </ToolbarButton>
          <ToolbarButton
            label="Заголовок 3"
            active={editor.isActive('heading', { level: 3 })}
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          >
            <Heading3 size={16} />
          </ToolbarButton>

          <ToolbarDivider />

          <ToolbarButton
            label="Нумерованный список"
            active={editor.isActive('orderedList')}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
          >
            <ListOrdered size={16} />
          </ToolbarButton>
          <ToolbarButton
            label="Маркированный список"
            active={editor.isActive('bulletList')}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
          >
            <ListIcon size={16} />
          </ToolbarButton>
          <ToolbarButton
            label="Очистить форматирование"
            onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}
          >
            <Eraser size={16} />
          </ToolbarButton>

          <ToolbarDivider />

          <ToolbarButton
            label="Цитата"
            active={editor.isActive('blockquote')}
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
          >
            <Quote size={16} />
          </ToolbarButton>
          <ToolbarButton label="Ссылка" active={editor.isActive('link')} onClick={openLinkModal}>
            <Link2 size={16} />
          </ToolbarButton>
          <ToolbarButton label="Изображение" onClick={() => imageInputRef.current?.click()}>
            <ImageIcon size={16} />
          </ToolbarButton>
          <ToolbarButton label="Разделитель" onClick={() => editor.chain().focus().setHorizontalRule().run()}>
            <Minus size={16} />
          </ToolbarButton>
          <ToolbarButton
            label="Код"
            active={editor.isActive('codeBlock')}
            onClick={() => editor.chain().focus().toggleCodeBlock().run()}
          >
            <Code2 size={16} />
          </ToolbarButton>

          <div className="ml-auto flex">
            <ToolbarButton label={fullscreen ? 'Свернуть' : 'На весь экран'} onClick={() => setFullscreen((v) => !v)}>
              {fullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
            </ToolbarButton>
          </div>
        </div>

        <input ref={imageInputRef} type="file" accept="image/*" onChange={onImageFileChange} className="hidden" />

        <EditorContent
          editor={editor}
          style={{ minHeight: fullscreen ? undefined : minHeight }}
          className="flex-1 overflow-y-auto px-4 py-3 text-sm text-gray-900 focus:outline-none [&_.ProseMirror]:min-h-[inherit] [&_.ProseMirror]:outline-none [&_.ProseMirror_p.is-editor-empty:first-child::before]:pointer-events-none [&_.ProseMirror_p.is-editor-empty:first-child::before]:float-left [&_.ProseMirror_p.is-editor-empty:first-child::before]:h-0 [&_.ProseMirror_p.is-editor-empty:first-child::before]:text-gray-400 [&_.ProseMirror_p.is-editor-empty:first-child::before]:content-[attr(data-placeholder)] [&_a]:text-red-600 [&_a]:underline [&_blockquote]:mb-3 [&_blockquote]:border-l-4 [&_blockquote]:border-gray-300 [&_blockquote]:pl-4 [&_blockquote]:text-gray-600 [&_blockquote]:italic [&_h1]:mb-2 [&_h1]:text-2xl [&_h1]:font-bold [&_h2]:mb-2 [&_h2]:text-xl [&_h2]:font-bold [&_h3]:mb-2 [&_h3]:text-lg [&_h3]:font-semibold [&_hr]:my-4 [&_hr]:border-gray-300 [&_img]:mb-3 [&_img]:max-w-full [&_img]:rounded-lg [&_li]:mb-1 [&_ol]:mb-3 [&_ol]:list-decimal [&_ol]:pl-5 [&_p:last-child]:mb-0 [&_p]:mb-3 [&_pre]:mb-3 [&_pre]:overflow-x-auto [&_pre]:rounded-md [&_pre]:bg-gray-100 [&_pre]:p-3 [&_pre]:font-mono [&_pre]:text-sm [&_ul]:mb-3 [&_ul]:list-disc [&_ul]:pl-5"
        />
      </div>

      {!fullscreen && error && (
        <p className="mt-1 text-sm text-redBase" role="alert">
          {error}
        </p>
      )}

      <LinkModal
        isOpen={isLinkModalOpen}
        initialValue={linkModalValue}
        onClose={() => setIsLinkModalOpen(false)}
        onSubmit={applyLink}
      />
    </div>
  );
};
